<template>
  <component :is="tag"
    :type="type"    
    class="fts-btn fts-btn-v2"
    :class="[btnClass, {'disabled': btnDisabled}]"
    :style="btnStyle"
    :disabled="btnDisabled"
    @mousedown="addRipple"
    @click="$emit('click')"
    ref="ftsBtn"
  >
    <transition-group class="ripples" name="grow" tag="div">
      <div class="ripple" v-for="ripple in ripples" :key="ripple.id" :style="rippleStyle(ripple)"></div>
    </transition-group>

    <div class="loading-overlay" v-if="loading && !disabled">
      <fts-spinner :size="14" />
    </div>

    <fts-moji :emoji="emoji" :size="emojiSize" v-if="emoji" />

    <slot />
  </component>
</template>

<script>
import FTSSpinner from "./FTSSpinner";
import FTSMoji from "./FTSMoji";

export default {
  props: {
    tag: {
      type: String,
      default: 'button',
      validator: value => ['button', 'a'].indexOf(value) > -1
    },
    type: {
      type: String,
      default: 'button',
      validator: value => ['button', 'submit'].indexOf(value) > -1
    },
    /* style */ 
    text: Boolean,
    outline: Boolean,
    rounded: Boolean,
    color: {
      type: String,
      default: 'default'
    },
    rippleColor: String,
    xs: Boolean,
    sm: Boolean,
    lg: Boolean,
    icon: Boolean,
    textTransformInit: Boolean,
    boxShadowNone: Boolean,
    fullWidth: Boolean,
    /* function */
    disabled: Boolean,
    loading: Boolean,
    /* others */
    emoji: String
  },
  components: {
    "fts-spinner": FTSSpinner,
    "fts-moji": FTSMoji
  },
  data: () => ({
    ripples: [],
    emojiSize: 14
  }),
  computed: {
    theme() {
      const themes = ['primary', 'secondary', 'success', 'danger', 'warning', 'dark', 'ternary-green', 'ternary-blue', 'ai', 'default']
      try {
        return themes.find(x => x === this.color.toString().toLowerCase())
      } catch(err) { 
        console.warn('error color');
      }
    },
    btnClass() {
      let classes = "fts-btn-default";
      if (this.theme) 
        classes =  `fts-btn-${this.theme}`;
      else 
        classes += " btn-custom";
      /* custom:ripple color */
      if (this.rippleColor) 
        classes += " ripple-custom";
      /* style */
      if (this.text) 
        classes += " fts-btn-text";
      else if (this.outline) 
        classes +=  " fts-btn-outline";
      else 
        classes += " fts-btn-raised";
      /* size */
      if (this.xs) {
        classes += " fts-btn-xs";
        this.emojiSize = 12;
      } else if (this.sm) {
        classes += " fts-btn-sm";
        this.emojiSize = 12;
      } else if (this.lg) {
        classes += " fts-btn-lg";
        this.emojiSize = 16;
      } else {
        classes += " fts-btn-md";
      }
      /* width */
      if (this.fullWidth) {
        classes += " fts-btn-w-full";
      }
      /* box shadow */
      if (this.boxShadowNone)
        classes += " none-bs";
      /* icon */
      if (this.icon)
        classes += " fts-btn-icon";
      /* rounded */
      if (this.rounded)
        classes += " fts-btn-rounded";
      /* text transform */
      if (this.textTransformInit)
        classes += " text-transform-init";

      return classes
    },
    btnStyle() {
      if (!this.theme && !this.btnDisabled) {
        if (this.outline) {
          return {
            borderColor: this.color,
            color: this.color
          } 
        } else if (this.text) {
          return {
            color: this.color
          }
        } else {
          return {
            backgroundColor: this.color
          }
        }
      }
    },
    btnDisabled() {
      return this.disabled || this.loading
    }
  },
  methods: {
    rippleStyle(ripple) {
      let style = {
        top: ripple.top,
        left: ripple.left,
        width: ripple.width,
        height: ripple.height,
      }
      if (this.rippleColor)
        style.background = this.rippleColor
      return style
    },
    addRipple(e) {
      if (e.button !== 0)
        return

      try {
        const { left, top } = this.$refs.ftsBtn.getBoundingClientRect();
        const rippleId = Date.now();
        this.ripples.push({
          width : `${this.rippleWidth}px`,
          height: `${this.rippleWidth}px`,
          left  : `${e.clientX - left - this.halfRippleWidth}px`,
          top   : `${e.clientY - top - this.halfRippleWidth}px`,
          id    : rippleId
        });
      } catch(err) {
        console.warn("[DOMException]", err);
      }
    },
    purgeRipples() {
      this.ripples = [];
    }
  },
  mounted() {
    const width = this.$refs.ftsBtn.offsetWidth;
    const height = this.$refs.ftsBtn.offsetHeight;
    this.rippleWidth = width > height ? width : height;
    this.halfRippleWidth = this.rippleWidth / 2;  
    window.addEventListener("mouseup", this.purgeRipples);
  },
  beforeDestroy() {
    window.removeEventListener("mouseup", this.purgeRipples)
  },
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

@mixin initial($type) {
  $buttons: (
    'primary': fts-primary-rgb, 
    'secondary': fts-secondary-rgb,
    'success': fts-success-rgb,
    'danger': fts-danger-rgb,
    'warning': fts-warning-rgb,
    'dark': fts-dark-rgb,
    'ternary-green': fts-ternary-green-rgb,
    'ternary-blue': fts-ternary-blue-rgb,
    'ai': fts-ai-rgb,
    'default': fts-default-rgb
  );
  @each $name, $color in $buttons {
    &.fts-btn-#{$name} {
      @if ($type == outline or $type == text)  {
        color: rgb(var(--#{$color}));
        background-color: transparent;
        .ripple {
          background-color: rgba(var(--#{$color}), 0.15);
        }
      } 
      @else if $type == raised {
        background-color: rgb(var(--#{$color}));
        color: #fff;
        &:hover {
          background-color: rgba(var(--#{$color}), 0.9);
        }
        .ripple {
          background-color: rgba(255, 255, 255, 0.3)
        }
      }
      @if $type == outline {
        border: 1px solid rgb(var(--#{$color}));
      } 
    }
  }
}
/* custom:background */
@mixin beforeBgCustom() {
  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: currentColor;
    opacity: 0;
    transition: opacity 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    z-index: -1;
  }
  &:hover:before {
    opacity: 0.1;
  }
}
/* btn:outline, text */
@mixin outlineTextCustom() {
  &.btn-custom {
    &:not(.ripple-custom) {
      .ripple {
        background-color: currentColor;
      }    
      .grow-enter {
        transform: scale(0);
        opacity: .15;
      }
      .grow-enter-to {
        transform: scale(4);
        opacity: .15;
      }
      .grow-leave {
        transform: scale(4);
        opacity: .15;
      }  
    }
  }
}
/* size */
@mixin size() {
  $sizes: 
    xs 0.625rem 1.5 0.125rem 0.25rem,
    sm 0.75rem 1.5 0.25rem 0.5rem,
    md 0.875rem 1.5 0.375rem 0.75rem,
    lg 1rem 1.5 0.375rem 1rem
  ;
  
  @each $name, $fontSize, $lineHeight, $py, $px in $sizes {
    $size: ($fontSize * $lineHeight) + (2 * $py) + 0.125rem;
    @if $name == md {
      & {
        min-height: $size;
        padding-left: $px;
        padding-right: $px;
        font-size: $fontSize;
        line-height: $lineHeight;
        &.fts-btn-icon {
          width: $size;
          min-width: $size;
          max-width: $size;
          height: $size;
          min-height: $fontSize;
          max-height: $size;
        }
      }
    } @else {
      &.fts-btn-#{$name} {
        min-height: $size;
        padding-left: $px;
        padding-right: $px;
        font-size: $fontSize;
        line-height: $lineHeight;
        &.fts-btn-icon {
          width: $size;
          min-width: $size;
          max-width: $size;
          height: $size;
          min-height: $fontSize;
          max-height: $size;
        }
      }
    }
  }
}
// Will remove 'v2' after change legacy fts-btn
.fts-btn.fts-btn-v2 {
  @include user-select-none;
  font-family: $font-family;
	-moz-osx-font-smoothing: grayscale;
	-webkit-font-smoothing: antialiased;
  position: relative;
  display: inline-flex;
	align-items: center;
	justify-content: center;
	border: none;
	border-radius: 0.25rem;
	outline: none;
  font-weight: 500;
	letter-spacing: .0892857143em;
  white-space: nowrap;
	text-decoration: none;
	box-sizing: border-box;
	-webkit-appearance: none;
	overflow: hidden;
  z-index: 0;
  user-select: none;
  -webkit-user-drag: none;
  &:hover:not(:disabled):not(.disabled) {
		cursor: pointer;
	}
  &:disabled,
  &.disabled,
  &:disabled:hover,
  &.disabled:hover {
    &:before {
      background-color: transparent !important;
    }
    box-shadow: none !important;
    border-color: transparent !important;
    background-color: transparent !important;
    color: var(--fts-disabled) !important;
    cursor: not-allowed !important;
    span {
      color: var(--fts-disabled) !important;
    }
  } 
  /* btn:raised */
  &.fts-btn-raised {
    &:not(.disabled) {
      @include initial(raised);      
      &:not(.none-bs) {
        box-shadow: $btn-box-shadow;
        transition: box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1); 
        &:hover {
          box-shadow: $btn-box-shadow-hover;
        }
      }
      /* custom:raised */
      &.btn-custom {
        @include beforeBgCustom;
      }
    }
  }
  /* btn:outline */
  &.fts-btn-outline {
    &:not(.disabled) {
      @include initial(outline);
      @include outlineTextCustom;
      @include beforeBgCustom;
      transition: background 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    }
    &.disabled {
      border: 1px solid transparent;
    }
  }
  /* btn:text */
  &.fts-btn-text {
    &:not(.disabled) {
      @include initial(text);
      @include outlineTextCustom;
      @include beforeBgCustom;
      transition: background 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    }
  }
  &.fts-btn-icon {
    padding: 0;
  }
  &.fts-btn-icon,
  &.fts-btn-rounded {
    border-radius: 10em;
  }
  &:not(.fts-btn-icon) {
    svg, i {
      margin-right: 0.375rem;
    }
  }
  &:not(.text-transform-init) {
    text-transform: uppercase;
  }
  /* width */
  &.fts-btn-w-full {
    width: 100%;
  }
  @include size;
  /* loading overlay */
  .loading-overlay {
    @include position($top: 0, $left: 0, $right: 0, $bottom: 0);
    @include flex-center-middle;
    @include theme() {
      background-color: rgba($color: theme-get('fts-gray-100'), $alpha: .75);
    }
    svg {
      margin: 0;
    }
  }
  /* ripples */
  .ripples {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    pointer-events: none;
  }
  .ripple {
    width: 100%;
    height: 100%;
    position: absolute;
    border-radius: 50%;
    opacity: 0;
    pointer-events: none;
  }
  /* transition */
  .grow-enter-active, .grow-enter-to-active {
    transition: all 1000ms ease-out;
  }
  .grow-leave-active, .grow-leave-to-active {
    transition: all 300ms ease-out;
  }
  .grow-enter {
    transform: scale(0);
    opacity: 1;
  }
  .grow-enter-to {
    transform: scale(4);
    opacity: 1;
  }
  .grow-leave {
    transform: scale(4);
    opacity: 1;
  }
  .grow-leave-to {
    transform: scale(4);
    opacity: 0;
  }
}
</style>